/**
 * The one way a writer reports a change.
 *
 * A store or action that changes an object calls one of these. They resolve the
 * actor, word the summary from the object's own state labels, and write to the
 * shared history, so a change reads the same wherever it was made.
 */

import { getAuthSession } from "@/lib/auth-session"

import { recordObjectChange } from "./history"
import { definitionOf } from "./registry"
import type { HistoryEntry, ObjectType } from "./types"

import "./definitions"

/** Who is acting. The signed-in identifier, or a neutral word when there is none. */
export function currentActor(): string {
  try {
    return getAuthSession() ?? "You"
  } catch {
    return "You"
  }
}

export function trackCreated(type: ObjectType, objectId: string, detail?: string) {
  return recordObjectChange({
    type,
    objectId,
    by: currentActor(),
    summary: detail ? `Created · ${detail}` : "Created",
  })
}

export function trackDeleted(type: ObjectType, objectId: string) {
  return recordObjectChange({ type, objectId, by: currentActor(), summary: "Deleted" })
}

/** "Moved from Draft to Published", worded from the object's own labels. */
export function trackStatusChange(
  type: ObjectType,
  objectId: string,
  from: string | undefined,
  to: string,
) {
  if (from === to) return undefined
  const states = definitionOf(type).status?.states ?? {}
  const label = (key: string | undefined) => (key ? (states[key]?.label ?? key) : undefined)
  return recordObjectChange({
    type,
    objectId,
    by: currentActor(),
    summary: from ? `Moved from ${label(from)} to ${label(to)}` : `Set to ${label(to)}`,
    changes: [{ field: "Status", from: label(from), to: label(to) }],
  })
}

export function trackEdited(
  type: ObjectType,
  objectId: string,
  summary: string,
  changes?: HistoryEntry["changes"],
) {
  return recordObjectChange({ type, objectId, by: currentActor(), summary, changes })
}
